/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import { Vector3 } from 'three';
import { Atom } from '../models/Atom.ts';
import { BasisAtom, CrystalState, LatticeParameters } from '../types.ts';

const DEG_TO_RAD = Math.PI / 180;

// Triclinic-to-Cartesian convention used here (the "standard" small-molecule
// crystallography setting):
//   a is along +x
//   b lies in the xy-plane
//   c is whatever the lattice angles require
// This matches the convention assumed by every common viewer (VESTA, Mercury,
// Jmol) so unit cells round-trip identically.
export const latticeVectors = (p: LatticeParameters): [Vector3, Vector3, Vector3] => {
  const alpha = p.alpha * DEG_TO_RAD;
  const beta = p.beta * DEG_TO_RAD;
  const gamma = p.gamma * DEG_TO_RAD;

  const cosA = Math.cos(alpha);
  const cosB = Math.cos(beta);
  const cosG = Math.cos(gamma);
  const sinG = Math.sin(gamma);

  const ax = new Vector3(p.a, 0, 0);
  const bx = new Vector3(p.b * cosG, p.b * sinG, 0);

  const cx = p.c * cosB;
  const cy = (p.c * (cosA - cosB * cosG)) / sinG;
  // Guard against rounding error pushing the radicand negative for
  // near-degenerate cells (e.g. cubic with α=β=γ=90°).
  const cz2 = p.c * p.c - cx * cx - cy * cy;
  const cz = Math.sqrt(Math.max(cz2, 0));
  const cv = new Vector3(cx, cy, cz);

  return [ax, bx, cv];
};

export const cellVolume = (p: LatticeParameters): number => {
  const [a, b, c] = latticeVectors(p);
  return Math.abs(a.dot(new Vector3().crossVectors(b, c)));
};

// The physical size of a FINITE structure (a nanostructure stored in its
// vacuum-padded orthogonal box): the span of its atoms along each box axis,
// in Å. The box is the bounding box plus vacuum (24 Å minimum), so the
// lattice parameters, the cell volume and the space group say nothing about
// the object; cards and tools report this instead.
export const finiteExtentAng = (crystal: CrystalState): { x: number; y: number; z: number } => {
  if (crystal.basis.length === 0) return { x: 0, y: 0, z: 0 };
  let minX = Infinity,
    minY = Infinity,
    minZ = Infinity,
    maxX = -Infinity,
    maxY = -Infinity,
    maxZ = -Infinity;
  for (const b of crystal.basis) {
    if (b.fx < minX) minX = b.fx;
    if (b.fx > maxX) maxX = b.fx;
    if (b.fy < minY) minY = b.fy;
    if (b.fy > maxY) maxY = b.fy;
    if (b.fz < minZ) minZ = b.fz;
    if (b.fz > maxZ) maxZ = b.fz;
  }
  const { a, b, c } = crystal.lattice;
  return { x: (maxX - minX) * a, y: (maxY - minY) * b, z: (maxZ - minZ) * c };
};

/** "2.10 × 2.10 × 3.05 nm" for a finite extent in Å. */
export const formatExtentNm = (e: { x: number; y: number; z: number }): string =>
  `${(e.x / 10).toFixed(2)} × ${(e.y / 10).toFixed(2)} × ${(e.z / 10).toFixed(2)} nm`;

export const fractionalToCartesian = (
  fx: number,
  fy: number,
  fz: number,
  vectors: [Vector3, Vector3, Vector3],
): Vector3 => {
  const [a, b, c] = vectors;
  return new Vector3(fx * a.x + fy * b.x + fz * c.x, fx * a.y + fy * b.y + fz * c.y, fx * a.z + fy * b.z + fz * c.z);
};

// Replicate the basis across a (nx × ny × nz) supercell for VISUALIZATION.
// Iterates i ∈ [0, nx], j ∈ [0, ny], k ∈ [0, nz] inclusive and emits the
// upper-bound copy of a basis atom only when that atom's corresponding
// fractional coordinate is at 0 — so a corner atom at (0,0,0) appears at
// all 8 cube corners, a face-center atom at (0.5, 0.5, 0) appears on both
// faces, an interior atom at (0.25, 0.25, 0.25) appears once. This matches
// textbook conventional-cell drawings (FCC Cu = 14 spheres, NaCl = 27,
// diamond = 18, etc.) rather than the compact P1 basis we store.
//
// Sequential indices start at `startIndex` so callers can splice into an
// existing atom list.
//
// Note: callers that need a strict compact-basis count (e.g. the supercell
// MD cap check) should use `supercellAtomCount` instead — it returns the
// unique-atom count without boundary duplication.
const FRACTIONAL_EPS = 1e-6;

export const expandSupercell = (
  crystal: CrystalState,
  nx: number,
  ny: number,
  nz: number,
  startIndex: number = 0,
): Atom[] => {
  const vectors = latticeVectors(crystal.lattice);
  const atoms: Atom[] = [];
  let idx = startIndex;
  for (let i = 0; i <= nx; i++) {
    for (let j = 0; j <= ny; j++) {
      for (let k = 0; k <= nz; k++) {
        for (const ba of crystal.basis) {
          // Skip the upper-boundary copy unless this basis atom is at the
          // origin face along that axis — otherwise interior / face / edge
          // atoms would be drawn one cell over from where they belong.
          if (i === nx && ba.fx > FRACTIONAL_EPS) continue;
          if (j === ny && ba.fy > FRACTIONAL_EPS) continue;
          if (k === nz && ba.fz > FRACTIONAL_EPS) continue;
          const pos = fractionalToCartesian(ba.fx + i, ba.fy + j, ba.fz + k, vectors);
          atoms.push(new Atom(idx++, ba.element, pos));
        }
      }
    }
  }
  return atoms;
};

// Replicate the basis across a (nx × ny × nz) supercell for SIMULATION.
// Unlike `expandSupercell` this iterates i ∈ [0, nx) (exclusive) and emits
// the FULL basis in every cell — no boundary-face duplication. That makes
// it the only correct expansion for molecular / covalent crystals: the
// visualization expansion duplicates individual atoms on the upper-boundary
// faces, which SPLITS any molecule straddling a face (the O at fx≈0 gets a
// far-face copy but its H atoms at fx>0 don't, or vice versa), leaving
// orphan atoms and cross-molecule auto-bonds that corrupt the MD topology.
// The result is exactly nx·ny·nz·basis.length atoms with every molecule
// intact, periodic, and consistent with the cell wireframe (which is also
// drawn over the exclusive [0, n) cell range).
export const expandSupercellCompact = (
  crystal: CrystalState,
  nx: number,
  ny: number,
  nz: number,
  startIndex: number = 0,
): Atom[] => {
  const vectors = latticeVectors(crystal.lattice);
  const atoms: Atom[] = [];
  let idx = startIndex;
  for (let i = 0; i < nx; i++) {
    for (let j = 0; j < ny; j++) {
      for (let k = 0; k < nz; k++) {
        for (const ba of crystal.basis) {
          const pos = fractionalToCartesian(ba.fx + i, ba.fy + j, ba.fz + k, vectors);
          atoms.push(new Atom(idx++, ba.element, pos));
        }
      }
    }
  }
  return atoms;
};

// Atoms in the asymmetric unit only (no replication). Convenience wrapper for
// gallery thumbnails that show a single unit cell.
export const unitCellAtoms = (crystal: CrystalState, startIndex: number = 0): Atom[] => {
  return expandSupercell(crystal, 1, 1, 1, startIndex);
};

// The eight corners of the unit-cell parallelepiped in Cartesian space. Useful
// for drawing the cell-edge wireframe over the atoms.
export const cellCorners = (p: LatticeParameters): Vector3[] => {
  const [a, b, c] = latticeVectors(p);
  const O = new Vector3(0, 0, 0);
  return [
    O.clone(),
    a.clone(),
    b.clone(),
    c.clone(),
    new Vector3().addVectors(a, b),
    new Vector3().addVectors(a, c),
    new Vector3().addVectors(b, c),
    new Vector3().addVectors(a, b).add(c),
  ];
};

// Indices into cellCorners() defining the 12 edges of the cell parallelepiped.
// Pair these up when drawing a Line / LineSegments wireframe.
export const CELL_EDGE_INDICES: ReadonlyArray<[number, number]> = [
  [0, 1],
  [0, 2],
  [0, 3],
  [1, 4],
  [1, 5],
  [2, 4],
  [2, 6],
  [3, 5],
  [3, 6],
  [4, 7],
  [5, 7],
  [6, 7],
];

// ── Conventional HCP hexagonal-prism depiction (VISUALIZATION ONLY) ──────────
// The stored HCP cell is the compact 2-atom primitive rhombic cell (a=b,
// γ=120°). The textbook figure everyone recognizes is the 17-atom hexagonal
// prism: 12 corner atoms (6 top + 6 bottom), 2 face-center atoms (top +
// bottom), and 3 interior atoms in the mid-plane. That prism is three of the
// primitive cells stacked into a hexagon, so it is NOT a periodic unit — it
// exists purely to draw the recognizable close-packed shape on the gallery
// card. Simulation / supercell expansion still key off `crystal.basis`.
//
// Geometry is built directly from a (= lattice.a, the in-plane nearest-
// neighbor distance) and c (= lattice.c, the stacking height):
//   - a regular hexagon of circumradius a (edge length = a) in the xy-plane,
//     replicated at z = 0 and z = c → 12 corners + the 2 face-center atoms
//   - 3 interior atoms at radius a/√3, angles {30°, 150°, 270°}, z = c/2
// For the ideal c/a = √(8/3) every interior atom sits exactly a from its
// neighbors (true close packing); for real c/a it is the standard depiction.
const HEX_CORNER_ANGLES = [0, 1, 2, 3, 4, 5].map((k) => (Math.PI / 3) * k);
const HEX_INTERIOR_ANGLES = [30, 150, 270].map((d) => d * DEG_TO_RAD);

// Should this crystal be DRAWN as the conventional hexagonal prism? True when
// the preset explicitly opts in (`conventionalCell`) OR — for crystals that
// round-tripped through Firestore before that flag existed, or were added to a
// saved/example project — when the structure is unmistakably a monatomic HCP
// metal: a metallic, hexagonal (a≈b, γ≈120°), 2-atom single-element cell.
// Graphite/graphene (not metallic), wurtzite (two elements), and molecular HCP
// (not metallic) all fail this test, so it won't false-positive on them.
export const isHcpPrismDepiction = (crystal: CrystalState): boolean => {
  if (crystal.conventionalCell === 'hexagonal-prism') return true;
  if (!crystal.metallic) return false;
  const { a, b, alpha, beta, gamma } = crystal.lattice;
  if (Math.abs(a - b) > 1e-3) return false;
  if (Math.abs(gamma - 120) > 0.5) return false;
  if (Math.abs(alpha - 90) > 0.5 || Math.abs(beta - 90) > 0.5) return false;
  if (crystal.basis.length !== 2) return false;
  return crystal.basis[0].element === crystal.basis[1].element;
};

export const hcpPrismAtoms = (crystal: CrystalState, startIndex: number = 0): Atom[] => {
  const { a, c } = crystal.lattice;
  const element = crystal.basis[0]?.element ?? 'C';
  const atoms: Atom[] = [];
  let idx = startIndex;
  // Top and bottom close-packed planes: 6 hexagon corners + 1 face center.
  for (const z of [0, c]) {
    for (const ang of HEX_CORNER_ANGLES) {
      atoms.push(new Atom(idx++, element, new Vector3(a * Math.cos(ang), a * Math.sin(ang), z)));
    }
    atoms.push(new Atom(idx++, element, new Vector3(0, 0, z)));
  }
  // Mid-plane interior atoms (the "B" layer of ABAB stacking).
  const r = a / Math.sqrt(3);
  for (const ang of HEX_INTERIOR_ANGLES) {
    atoms.push(new Atom(idx++, element, new Vector3(r * Math.cos(ang), r * Math.sin(ang), c / 2)));
  }
  return atoms;
};

// The 12 vertices of the hexagonal prism (bottom hexagon 0..5, top hexagon
// 6..11) in the same Cartesian frame as hcpPrismAtoms().
export const hcpPrismCorners = (p: LatticeParameters): Vector3[] => {
  const corners: Vector3[] = [];
  for (const z of [0, p.c]) {
    for (const ang of HEX_CORNER_ANGLES) {
      corners.push(new Vector3(p.a * Math.cos(ang), p.a * Math.sin(ang), z));
    }
  }
  return corners;
};

// Edges of the hexagonal prism: bottom hexagon (6) + top hexagon (6) + the 6
// verticals connecting them. Indices address hcpPrismCorners().
export const HEX_PRISM_EDGE_INDICES: ReadonlyArray<[number, number]> = [
  [0, 1],
  [1, 2],
  [2, 3],
  [3, 4],
  [4, 5],
  [5, 0],
  [6, 7],
  [7, 8],
  [8, 9],
  [9, 10],
  [10, 11],
  [11, 6],
  [0, 6],
  [1, 7],
  [2, 8],
  [3, 9],
  [4, 10],
  [5, 11],
];

// Total atom count in a (nx × ny × nz) supercell — pure arithmetic, no Atom
// allocation. The supercell-size dialog uses this to warn before expansion.
export const supercellAtomCount = (basis: BasisAtom[], nx: number, ny: number, nz: number): number => {
  return basis.length * nx * ny * nz;
};

// Fields that live on the LAB copy (chamberCrystal) only and never on the
// gallery entry: the force field the user picked in the lab toolbar / agent
// (forceFieldId) and the vacuum gap a melting experiment pads the box with
// (vacuumAng). Every path that puts a gallery entry into the lab must carry
// these across, or a lattice tweak in the Crystal Editor silently reset the
// user's Sutton–Chen choice back to the default EAM. (dopants / defects are
// written to BOTH copies by their popovers, so they need no carry-over.)
//
// withLabOnlyFields returns a fresh lab copy of `next` that keeps those fields
// from the crystal currently in the lab, when `prev` is the same crystal (by
// name). Always a new object, so the viewer's / worker's rebuild effects keyed
// on chamberCrystal identity still fire. A different crystal (or none) gets
// `next` back as-is — its lab-only settings are its own. A value already set
// on `next` wins over the lab's.
export const withLabOnlyFields = (next: CrystalState, prev: CrystalState | null | undefined): CrystalState => {
  if (!prev || prev.name !== next.name) return next;
  const out: CrystalState = { ...next };
  if (next.forceFieldId === undefined && prev.forceFieldId !== undefined) out.forceFieldId = prev.forceFieldId;
  if (next.vacuumAng === undefined && prev.vacuumAng !== undefined) out.vacuumAng = prev.vacuumAng;
  return out;
};

// Shrink a supercell until it fits under the atom cap, one repeat at a time
// off the LONGEST supercell edge (so the block stays as cubic as possible).
// The cap is a hard limit everywhere a crystal enters the lab: the scaffold
// builder's bond pass is O(N²) and the viewer mounts one mesh per atom, so a
// 20×20×20 silicon block (64,000 atoms) froze the tab for minutes. Returns the
// fitted tiling and whether anything was shrunk. A basis that is itself over
// the cap comes back at 1×1×1 — nothing more can be done for it here.
export const fitSupercellToCap = (
  basisCount: number,
  lattice: LatticeParameters,
  supercell: { nx: number; ny: number; nz: number },
  cap: number,
): { nx: number; ny: number; nz: number; shrunk: boolean } => {
  let { nx, ny, nz } = supercell;
  nx = Math.max(1, Math.round(nx));
  ny = Math.max(1, Math.round(ny));
  nz = Math.max(1, Math.round(nz));
  let shrunk = false;
  while (basisCount * nx * ny * nz > cap) {
    const edges: [number, number][] = [
      [lattice.a * nx, nx],
      [lattice.b * ny, ny],
      [lattice.c * nz, nz],
    ];
    let axis = -1;
    let longest = -Infinity;
    for (let k = 0; k < 3; k++) {
      if (edges[k][1] > 1 && edges[k][0] > longest) {
        longest = edges[k][0];
        axis = k;
      }
    }
    if (axis < 0) break;
    if (axis === 0) nx--;
    else if (axis === 1) ny--;
    else nz--;
    shrunk = true;
  }
  return { nx, ny, nz, shrunk };
};

// IUPAC inorganic element priority for formula ordering (Red Book Table VI):
// electropositive elements first, electronegative last. Drives the canonical
// order of binary/ternary inorganic formulas — Na before Cl ("NaCl" not
// "ClNa"), Sr before Ti before O ("SrTiO3"), Zn before O ("ZnO").
const IUPAC_INORGANIC_ORDER: readonly string[] = [
  'Rn',
  'Xe',
  'Kr',
  'Ar',
  'Ne',
  'He',
  'Fr',
  'Cs',
  'Rb',
  'K',
  'Na',
  'Li',
  'Ra',
  'Ba',
  'Sr',
  'Ca',
  'Mg',
  'Be',
  'Lr',
  'No',
  'Md',
  'Fm',
  'Es',
  'Cf',
  'Bk',
  'Cm',
  'Am',
  'Pu',
  'Np',
  'U',
  'Pa',
  'Th',
  'Ac',
  'Lu',
  'Yb',
  'Tm',
  'Er',
  'Ho',
  'Dy',
  'Tb',
  'Gd',
  'Eu',
  'Sm',
  'Pm',
  'Nd',
  'Pr',
  'Ce',
  'La',
  'Y',
  'Sc',
  'Rf',
  'Hf',
  'Zr',
  'Ti',
  'Db',
  'Ta',
  'Nb',
  'V',
  'Sg',
  'W',
  'Mo',
  'Cr',
  'Bh',
  'Re',
  'Tc',
  'Mn',
  'Hs',
  'Os',
  'Ru',
  'Fe',
  'Mt',
  'Ir',
  'Rh',
  'Co',
  'Ds',
  'Pt',
  'Pd',
  'Ni',
  'Rg',
  'Au',
  'Ag',
  'Cu',
  'Cn',
  'Hg',
  'Cd',
  'Zn',
  'Nh',
  'Tl',
  'In',
  'Ga',
  'Al',
  'B',
  'Fl',
  'Pb',
  'Sn',
  'Ge',
  'Si',
  'C',
  'Mc',
  'Bi',
  'Sb',
  'As',
  'P',
  'N',
  'H',
  'Lv',
  'Po',
  'Te',
  'Se',
  'S',
  'O',
  'Ts',
  'At',
  'I',
  'Br',
  'Cl',
  'F',
];
const IUPAC_RANK = new Map<string, number>(IUPAC_INORGANIC_ORDER.map((el, i) => [el, i]));
// Deuterium and tritium are hydrogen: they take hydrogen's place in the
// order (just after H itself, so semi-heavy water reads HDO). The element
// gate accepts D and T, but they were missing here and sorted LAST — heavy
// water printed as "OD₂".
{
  const h = IUPAC_RANK.get('H');
  if (h !== undefined) {
    IUPAC_RANK.set('D', h + 0.1);
    IUPAC_RANK.set('T', h + 0.2);
  }
}

// Render a positive integer as Unicode subscripts (3 → "₃", 12 → "₁₂"). The
// preset names use Unicode subscripts (SrTiO₃, CaF₂), so chemicalFormula
// emits them too — keeps the FORMULA card label and the popover Formula row
// typographically conventional, and lets the card overlay's strip-formula
// regex find an exact match in names like "SrTiO₃ (Perovskite)".
const SUBSCRIPTS: readonly string[] = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
const toSubscript = (n: number): string =>
  String(n)
    .split('')
    .map((d) => SUBSCRIPTS[+d] ?? d)
    .join('');

// Reduce element counts by their GCD so the chemical formula is the empirical
// (simplest-integer-ratio) form: 4 Na + 4 Cl → "NaCl", 8 Si → "Si", 1 Sr + 1
// Ti + 3 O → "SrTiO₃". Element order follows IUPAC inorganic priority —
// electropositive elements first, electronegative last — so NaCl is "NaCl"
// (not the alphabetic "ClNa") and SrTiO₃ lands in its conventional form.
export const chemicalFormula = (basis: BasisAtom[]): string => {
  if (basis.length === 0) return '';
  const counts = new Map<string, number>();
  for (const a of basis) {
    counts.set(a.element, (counts.get(a.element) ?? 0) + 1);
  }
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  let divisor = 0;
  for (const n of counts.values()) divisor = divisor === 0 ? n : gcd(divisor, n);
  if (divisor <= 0) divisor = 1;
  const entries = Array.from(counts.entries()).map(([el, n]) => [el, n / divisor] as [string, number]);
  entries.sort((a, b) => {
    const ra = IUPAC_RANK.get(a[0]) ?? Number.MAX_SAFE_INTEGER;
    const rb = IUPAC_RANK.get(b[0]) ?? Number.MAX_SAFE_INTEGER;
    if (ra !== rb) return ra - rb;
    return a[0].localeCompare(b[0]);
  });
  return entries.map(([el, n]) => (n === 1 ? el : `${el}${toSubscript(n)}`)).join('');
};

// Smallest lattice length (Å) the editors accept. A zero or negative edge
// collapses the cell: the lattice vectors go degenerate, every Cartesian
// position lands on a plane or a point, and the bond pass / MD turn into NaN.
// Real cells start around 2.5 Å; the floor is well under that so an unusual
// cell is never refused, while a cleared or mistyped field cannot commit.
export const MIN_LATTICE_LENGTH = 0.5;

// Is this a usable lattice: finite, every edge at or above the floor, every
// angle strictly inside (0°, 180°)? One rule for the per-card editor, the
// agent's editLattice, and anything else that writes LatticeParameters.
export const isValidLattice = (p: LatticeParameters): boolean => {
  const { a, b, c, alpha, beta, gamma } = p;
  for (const len of [a, b, c]) if (!Number.isFinite(len) || len < MIN_LATTICE_LENGTH) return false;
  for (const ang of [alpha, beta, gamma]) if (!Number.isFinite(ang) || ang <= 0 || ang >= 180) return false;
  // Each angle can be valid alone while the three together describe no cell
  // (α = β = γ = 150°, or α = β = 10° with γ = 90°): the cell volume factor
  // 1 − cos²α − cos²β − cos²γ + 2 cosα cosβ cosγ is then ≤ 0, latticeVectors
  // clamps the c-vector's height to zero and every atom lands on one plane.
  const [ca, cb, cg] = [alpha, beta, gamma].map((d) => Math.cos((d * Math.PI) / 180));
  const volumeFactor = 1 - ca * ca - cb * cb - cg * cg + 2 * ca * cb * cg;
  return volumeFactor > 1e-6;
};

// Centroid of a set of atoms (the point the gallery thumbnail centers on).
// Null for an empty set so a caller can fall back to the cell center.
export const atomCentroid = (atoms: ReadonlyArray<Atom>): Vector3 | null => {
  if (atoms.length === 0) return null;
  const c = new Vector3();
  for (const a of atoms) c.add(a.position);
  return c.divideScalar(atoms.length);
};

// A gallery name no existing crystal uses: `desired` itself, else
// "desired (2)", "desired (3)", … Lab state (chamberCrystal), the saved MD
// frame and the per-card editor all find a crystal BY NAME, so two entries
// sharing one would make every one of them ambiguous. `taken` is the set of
// names in use — normally every gallery entry plus the lab's copy (see
// takenCrystalNames); a rename passes its own old name through `exclude` so
// renaming a card to a variant of its own name is not pushed to " (2)".
export const uniqueCrystalName = (
  desired: string,
  taken: Iterable<string | null | undefined>,
  exclude?: string | null,
): string => {
  const used = new Set<string>();
  for (const n of taken) if (n && n !== exclude) used.add(n);
  if (!used.has(desired)) return desired;
  for (let n = 2; ; n++) {
    const candidate = `${desired} (${n})`;
    if (!used.has(candidate)) return candidate;
  }
};

// Every crystal name the project is using: the gallery entries and the lab's
// copy. The lab copy normally mirrors a gallery entry, but it can outlive one
// (a crystal deleted from the gallery while loaded stays in the lab until
// something replaces it); a new entry must not take that name, or the lab
// would claim to be showing it.
export const takenCrystalNames = (ps: {
  crystals?: ReadonlyArray<{ name?: string }> | null;
  chamberCrystal?: { name?: string } | null;
}): string[] => {
  const out: string[] = [];
  for (const c of ps.crystals ?? []) if (c.name) out.push(c.name);
  if (ps.chamberCrystal?.name) out.push(ps.chamberCrystal.name);
  return out;
};
